import type { BotTier } from '@runkr/config'
import { JUMP } from '../inputBits'
import type { InputBits } from '../inputBits'
import type { Rng } from '../rng'
import { spansOverlap } from '../geom'
import { nearby } from '../track/xindex'
import type { BotMemory, World } from '../world'
import { plan } from './perception'
import type { View } from './perception'
import { rollout } from './rollout'
import { chance } from './variety'

/** Highest a climb can reach: a full jump plus a full double jump (MAP_01 J2 ≈ 250 px). */
function reachPx(world: World): number {
  const {
    jumpVelocityPxPerTick: v1,
    doubleJumpVelocityPxPerTick: v2,
    gravityPxPerTick2: g,
  } = world.cfg
  return (v1 * v1 + v2 * v2) / (2 * g)
}

// Route ids share `BotMemory.routeId`: platforms by index, floating ramps after them.
const RAMP_ID_BASE = 1_000_000

/** An upper route ahead: where it starts and the height a climb onto it must reach. */
export interface Route {
  id: number
  x: number
  /** Standing at or above this y counts as on the route (above a bounce cap: higher still). */
  climbTo: number
}

/**
 * The nearest upper route starting ahead within `lookaheadPx` of the bot's front edge: a one-way
 * platform or the low end of a floating up-slab at least `climbMinPx` above the feet and within
 * jump reach. A platform with a bounce pad on it (a mushroom cap) launches the bot, so only
 * standing higher than the cap counts. `null` when there is none.
 */
export function upperRouteAhead(world: World, i: number, tier: BotTier): Route | null {
  const p = world.players[i]!
  const { track } = world
  const front = p.x + p.w
  const x1 = front + tier.lookaheadPx
  const top = p.y - tier.climbMinPx
  const bottom = p.y - reachPx(world)
  let best: Route | null = null
  const consider = (id: number, x: number, y: number, climbTo: number): void => {
    if (x < front || x > x1 || y > top || y < bottom) return
    if (!best || x < best.x) best = { id, x, climbTo }
  }
  for (const k of nearby(track.index.platforms, front, x1)) {
    const pl = track.platforms[k]!
    if (pl.kind === 'moving') continue
    const cap = track.zones.some(
      (z) => z.kind === 'bouncePad' && z.y === pl.y && spansOverlap(pl.x, pl.w, z.x, z.w),
    )
    consider(k, pl.x, pl.y, cap ? pl.y - 1 : pl.y)
  }
  for (const k of nearby(track.index.ramps, front, x1)) {
    const r = track.ramps[k]!
    if (r.dir === 'up') consider(RAMP_ID_BASE + k, r.x, r.y + r.h, r.y + r.h)
  }
  return best
}

/**
 * Take an upper route (MAP_01 §4: bots spread across the tiers): at each new route ahead the bot
 * rolls its `upperRoutePct` once; if it goes up, it jumps (or jumps and double-jumps) at the first
 * tick the forecast lands it on the route. Grounded bots only. Returns the JUMP press, and arms
 * `airJump` for a planned double jump.
 */
export function climb(
  world: World,
  i: number,
  mem: BotMemory,
  view: View,
  tier: BotTier,
  rng: Rng,
): InputBits {
  const p = world.players[i]!
  if (!p.grounded || (p.prevBits & JUMP) !== 0) return 0
  const route = upperRouteAhead(world, i, tier)
  if (!route) return 0
  if (route.id !== mem.routeId) {
    mem.routeId = route.id
    mem.routeUp = chance(rng, mem.upperRoutePct)
  }
  if (!mem.routeUp) return 0
  for (const action of ['hold', 'double'] as const) {
    const o = rollout(world, i, view.scene, {
      ...plan(view, mem, action, 0, Infinity),
      climbTo: route.climbTo,
    })
    if (!o.ok) continue
    mem.holdJump = true
    mem.airJump = action === 'double'
    return JUMP
  }
  return 0
}

/**
 * The second half of a planned double jump, mirroring the rollout's `double` action: in the air,
 * once the first jump has peaked, release JUMP for a tick, then press it again. `null` when no
 * air jump is armed (or it was dropped on landing).
 */
export function airJump(world: World, i: number, mem: BotMemory): InputBits | null {
  const p = world.players[i]!
  if (!mem.airJump) return null
  if (p.grounded) {
    mem.airJump = false
    return null
  }
  if (p.vy < 0) return JUMP
  if ((p.prevBits & JUMP) !== 0) return 0
  mem.airJump = false
  mem.holdJump = true
  return JUMP
}
